import { SELECTED_ACCOUNT_CHAIN_ID, bytes32Schema, nonzeroAddressSchema } from '@hoodmorning/contracts';
import { keccak256, toHex, type Hex } from 'viem';
import { z } from 'zod';

export const TERMINAL_RECEIPT_RUNTIME_MAX_REQUEST_BYTES = 256 * 1024;
export const TERMINAL_RECEIPT_RUNTIME_MAX_RESPONSE_BYTES = 1024 * 1024;
export const TERMINAL_RECEIPT_RUNTIME_REQUEST_TIMEOUT_MS = 8_000;
export const TERMINAL_RECEIPT_RUNTIME_MAXIMUM_DURATION_MS = 120_000;
/** A seven-hop V3 receipt proof currently needs 78 bounded provider reads. */
export const TERMINAL_RECEIPT_RUNTIME_MAXIMUM_REQUESTS = 96;

const codeHash = bytes32Schema.refine(value => !/^0x0{64}$/i.test(value));
const sessionHash = z.string().regex(/^[a-f0-9]{64}$/).refine(value => !/^0{64}$/.test(value));
const method = z.enum([
  'eth_chainId',
  'eth_getBlockByNumber',
  'eth_getCode',
  'eth_getStorageAt',
  'eth_call',
  'eth_getTransactionReceipt',
  'eth_getTransactionByHash',
  'eth_getUserOperationReceipt',
]);

export const terminalReceiptRuntimeRequestSchema = z.strictObject({ intentId: z.uuid() });

/** Short-lived server-signed read capability. The MAC is session-token bound;
 * this public record grants no signing, submission, accounting or retry port. */
export const terminalReceiptRuntimeAuthorizationSchema = z.strictObject({
  schemaVersion: z.literal(1),
  chainId: z.literal(SELECTED_ACCOUNT_CHAIN_ID),
  userId: z.string().min(1).max(128),
  sessionHash,
  intentId: z.uuid(),
  vault: nonzeroAddressSchema,
  userOperationHash: codeHash,
  historyHash: codeHash,
  sourceHash: codeHash,
  releaseHash: codeHash,
  quoteConfigurationHash: codeHash,
  issuedAtMs: z.number().int().safe().nonnegative(),
  expiresAtMs: z.number().int().safe().positive(),
}).superRefine((value, context) => {
  if (value.expiresAtMs - value.issuedAtMs !== TERMINAL_RECEIPT_RUNTIME_MAXIMUM_DURATION_MS) {
    context.addIssue({ code: 'custom', message: 'Receipt authorization lifetime mismatch' });
  }
});

export const terminalReceiptRuntimeDescriptorSchema = z.strictObject({
  schemaVersion: z.literal(1),
  chainId: z.literal(SELECTED_ACCOUNT_CHAIN_ID),
  userId: z.string().min(1).max(128),
  intentId: z.uuid(),
  vault: nonzeroAddressSchema,
  userOperationHash: codeHash,
  historyHash: codeHash,
  sourceHash: codeHash,
  releaseJson: z.string().min(2).max(64 * 1024),
  releaseHash: codeHash,
  quoteConfigurationJson: z.string().min(2).max(4_096),
  quoteConfigurationHash: codeHash,
  authorizationJson: z.string().min(2).max(4_096),
  authorizationMac: codeHash,
  limits: z.strictObject({
    maximumRequests: z.literal(TERMINAL_RECEIPT_RUNTIME_MAXIMUM_REQUESTS),
    maximumDurationMs: z.literal(TERMINAL_RECEIPT_RUNTIME_MAXIMUM_DURATION_MS),
    requestTimeoutMs: z.literal(TERMINAL_RECEIPT_RUNTIME_REQUEST_TIMEOUT_MS),
    maximumRequestBytes: z.literal(TERMINAL_RECEIPT_RUNTIME_MAX_REQUEST_BYTES),
    maximumResponseBytes: z.literal(TERMINAL_RECEIPT_RUNTIME_MAX_RESPONSE_BYTES),
  }),
  receiptObservationAuthorized: z.literal(true),
  signingAuthorized: z.literal(false),
  submissionAuthorized: z.literal(false),
  executionAuthorized: z.literal(false),
  resubmissionAuthorized: z.literal(false),
  accountingEligible: z.literal(false),
  feeSettlementAuthorized: z.literal(false),
  automaticOrderAuthorized: z.literal(false),
});

export const terminalReceiptRuntimeRpcSchema = z.strictObject({
  schemaVersion: z.literal(1),
  intentId: z.uuid(),
  historyHash: codeHash,
  sourceHash: codeHash,
  releaseHash: codeHash,
  quoteConfigurationHash: codeHash,
  authorizationJson: z.string().min(2).max(4_096),
  authorizationMac: codeHash,
  method,
  params: z.array(z.unknown()).max(16),
});

export const terminalReceiptRuntimeRpcResponseSchema = z.strictObject({
  schemaVersion: z.literal(1),
  result: z.unknown(),
});

/** A private read of one already-published native proof, never a browser trace
 * proxy. It uses the same session/history-bound read lease as ordinary receipts. */
export const terminalNativeReceiptRequestSchema = terminalReceiptRuntimeRpcSchema.omit({ method: true, params: true });
const quantity = z.string().regex(/^(0|[1-9][0-9]{0,77})$/)
  .refine(value => BigInt(value) < 1n << 256n);
const asset = z.union([z.literal('NATIVE'), nonzeroAddressSchema]);
const nativeDisplayBase = {
  schemaVersion: z.literal(1), kind: z.literal('stored-native-terminal-receipt-display-v1'), chainId: z.literal(SELECTED_ACCOUNT_CHAIN_ID),
  intentId: z.uuid(), operationId: z.string().regex(/^[A-Za-z0-9:._-]{1,128}$/),
  vault: nonzeroAddressSchema, userOperationHash: codeHash, quoteId: z.string().min(1).max(1_024),
  callsHash: codeHash, originalConfigurationHash: codeHash,
  signingAuthorized: z.literal(false), submissionAuthorized: z.literal(false), executionAuthorized: z.literal(false),
  resubmissionAuthorized: z.literal(false), accountingEligible: z.literal(false), feeSettlementAuthorized: z.literal(false),
  automaticOrderAuthorized: z.literal(false),
};
const nativeDisplayConfirmed = {
  transactionHash: codeHash, blockHash: codeHash, blockNumber: quantity, confirmations: quantity,
  observedHeadNumber: quantity, observedHeadHash: codeHash, actualGasCost: quantity, actualGasUsed: quantity,
  finality: z.literal('confirmation-observed-not-irreversible'),
};
const nativeDisplayTrade = z.strictObject({ tokenIn: asset, tokenOut: asset, amountIn: quantity,
  swapGrossOutput: quantity, terminalFeeCredit: quantity, vaultNetCredit: quantity,
  feeBps: z.union([z.literal(45), z.literal(50)]) });
export const terminalNativeReceiptDisplaySchema = z.discriminatedUnion('status', [
  z.strictObject({ ...nativeDisplayBase, status: z.literal('pending') }),
  z.strictObject({ ...nativeDisplayBase, status: z.literal('reorg-uncertain') }),
  z.strictObject({ ...nativeDisplayBase, ...nativeDisplayConfirmed, status: z.literal('confirmed-success'), trade: nativeDisplayTrade }),
  z.strictObject({ ...nativeDisplayBase, ...nativeDisplayConfirmed, status: z.literal('confirmed-reverted'), trade: z.null() }),
]).superRefine((value, context) => {
  if (value.status !== 'confirmed-success' && value.status !== 'confirmed-reverted') return;
  if (BigInt(value.confirmations) < 2n || BigInt(value.observedHeadNumber) < BigInt(value.blockNumber)
    || BigInt(value.confirmations) !== BigInt(value.observedHeadNumber) - BigInt(value.blockNumber) + 1n)
    context.addIssue({ code: 'custom', message: 'Native receipt confirmation binding is invalid.' });
  if (value.trade !== null && ((value.trade.tokenIn === 'NATIVE') === (value.trade.tokenOut === 'NATIVE')
    || BigInt(value.trade.amountIn) === 0n
    || BigInt(value.trade.swapGrossOutput) !== BigInt(value.trade.terminalFeeCredit) + BigInt(value.trade.vaultNetCredit)
    || BigInt(value.trade.terminalFeeCredit) !== BigInt(value.trade.swapGrossOutput) * BigInt(value.trade.feeBps) / 10_000n))
    context.addIssue({ code: 'custom', message: 'Native receipt cashflow binding is invalid.' });
});
export type TerminalNativeReceiptDisplay = z.infer<typeof terminalNativeReceiptDisplaySchema>;

export type TerminalReceiptRuntimeDescriptor = z.infer<typeof terminalReceiptRuntimeDescriptorSchema>;
export type TerminalReceiptRuntimeRpc = z.infer<typeof terminalReceiptRuntimeRpcSchema>;
export type TerminalReceiptRuntimeAuthorization = z.infer<typeof terminalReceiptRuntimeAuthorizationSchema>;

/** Opaque commitment to the exact private receipt sources selected by the
 * host. It prevents one browser observation from mixing providers after a
 * configuration change without disclosing credential-bearing URLs. */
export function terminalReceiptSourceHash(input: Readonly<{ chain: string; bundler: string }>): Hex {
  if (!input.chain || !input.bundler) throw new Error('Terminal receipt sources are unavailable.');
  return keccak256(toHex(JSON.stringify({ schemaVersion: 1, purpose: 'terminal-receipt-sources',
    chain: input.chain, bundler: input.bundler })));
}

export type TerminalReceiptHistoryCommitmentInput = Readonly<{
  intent: unknown;
  serverFeeSnapshot: unknown;
  record: unknown;
  submission: Readonly<{ intentId: unknown; quoteId: unknown; evidence: unknown; reservation: unknown }>;
  vaultIdentity: unknown;
  receiptIdentityStatus: unknown;
}>;

const evmHexFields = new Set([
  'vault', 'tokenIn', 'tokenOut', 'feeRecipient', 'router', 'to', 'data', 'callsHash',
  'owner', 'sender', 'callData', 'userOperationHash', 'authenticatedSigner', 'initialOwner',
  'factory', 'factoryData', 'originalOwner', 'factoryCodeHash', 'curve', 'curveCodeHash',
  'baseToken', 'baseTokenCodeHash', 'quoteToken', 'quoteTokenCodeHash', 'policyHash',
  'preparedRecipient', 'sourceBlockHash',
]);

function canonicalJson(value: unknown, field?: string): unknown {
  if (value === null || typeof value === 'boolean') return value;
  // Normalize only schema-known EVM byte fields. Opaque identifiers such as a
  // provider request ID remain case-sensitive even if they happen to look hex.
  if (typeof value === 'string') return field && evmHexFields.has(field) && /^0x[0-9a-fA-F]*$/.test(value)
    ? value.toLowerCase() : value;
  if (typeof value === 'number' && Number.isFinite(value) && !Object.is(value, -0)) return value;
  if (Array.isArray(value)) return value.map(item => canonicalJson(item));
  if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.keys(value as Record<string, unknown>).sort()
      .map(key => [key, canonicalJson((value as Record<string, unknown>)[key], key)]));
  }
  throw new Error('Terminal receipt history commitment is not canonical JSON.');
}

/** Commits the immutable receipt inputs while deliberately excluding mutable
 * acknowledgement outcomes. A later outcome append must not invalidate a
 * read-only receipt session; any evidence, quote or identity change must. */
export function terminalReceiptHistoryHash(input: TerminalReceiptHistoryCommitmentInput): Hex {
  const projected = {
    schemaVersion: 1,
    intent: input.intent,
    serverFeeSnapshot: input.serverFeeSnapshot,
    record: input.record,
    submission: {
      intentId: input.submission.intentId,
      quoteId: input.submission.quoteId,
      evidence: input.submission.evidence,
      reservation: input.submission.reservation,
    },
    vaultIdentity: input.vaultIdentity,
    receiptIdentityStatus: input.receiptIdentityStatus,
  };
  return keccak256(toHex(JSON.stringify(canonicalJson(projected))));
}
